import { action, computed, observable, makeObservable } from 'mobx'
import { GetCurrentMember } from 'iam/SessionStore'
import { createObjectStorage, ObjectStorage, Storage } from 'infra/storage'
import {
  JobsTableColumns,
  StaticColumnDetails,
  MutableColumnDetails,
  ColumnId,
} from 'jobs/infra/JobsTableColumns'
import { AlertFactory, AlertState } from 'alerts/Alert'
import { csvDownload, CSVData } from 'utils/csv-util'
import { isNotNil } from 'utils/object-util'
import { move } from 'utils/array-util'
import SearchableJob from 'jobs/SearchableJob'
import { JobsExportAdapter } from './JobsExportAdapter'
import { ColumnDensity } from 'components/Table/TableCell'
import UserDataStore from 'iam/UserDataStore'
import { GetCurrentUserWorkspaceMemberships } from 'iam/WorkspaceMembershipStore'
import { Id } from 'types/definitions'
import { UserDataType } from 'iam/models/UserDataType'

/**
 * Generates default mutable details for local storage.
 */
const defaultSettings: MutableColumnDetails[] = JobsTableColumns.map(
  (column) => {
    return {
      id: column.id,
      visible: true,
    }
  },
)

/**
 * Jobs table column manager.
 */
export default class JobsTableColumnManager {
  /**
   * Loads column settings from local storage during activation
   */
  activate = () => this.loadColumnSettings()

  /**
   * Key used for storing column settings in local storage.
   * This key is for client and provider column settings.
   */
  private readonly _userKey = 'Jobs Table Column Settings - User'

  /**
   * Key used for storing column density setting in local storage.
   * This key is for client, provider, and admin column settings.
   */
  private readonly _densityKey = 'Jobs Table Density Setting - User'

  /**
   * Storage for column settings.
   */
  private storage: ObjectStorage

  /**
   * Returns all filtered jobs for export.
   */
  private getAllFilteredJobs: () => Promise<SearchableJob[]>

  /**
   * Getter for current member.
   */
  private getCurrentMember: GetCurrentMember

  /**
   * Getter for current user's workspace memberships.
   */
  private getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMemberships

  /**
   * Alert factory for alert creation.
   */
  private newAlert: AlertFactory

  /**
   * Columns to populate the jobs table header and rows with.
   */
  @observable columns: (StaticColumnDetails & MutableColumnDetails)[] = []

  /**
   * Determines open state of column manager dropdown.
   */
  @observable columnManagerOpen = false

  /**
   * The density of all columns in the table.
   */
  @observable density: ColumnDensity = ColumnDensity.Default

  /**
   * Constructor.
   */
  constructor(
    storage: Storage,
    getAllFilteredJobs: () => Promise<SearchableJob[]>,
    getCurrentMember: GetCurrentMember,
    getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMemberships,
    newAlert: AlertFactory,
    private userDataStore: UserDataStore,
    private getSelectedWorkspaces: () => string[],
    private keyPrefix: string,
  ) {
    makeObservable(this)
    this.storage = createObjectStorage(storage)
    this.getAllFilteredJobs = getAllFilteredJobs
    this.getCurrentMember = getCurrentMember
    this.getCurrentUserWorkspaceMemberships = getCurrentUserWorkspaceMemberships
    this.newAlert = newAlert
  }

  /**
   * Jobs Export Adapter.
   */
  private adapter = JobsExportAdapter(
    () => this.getAllFilteredJobs(),
    () => this.filteredColumns.filter((col) => !col.actionSelector),
  )

  get userKey() {
    return `${this.keyPrefix} ${this._userKey}`
  }

  get densityKey() {
    return `${this.keyPrefix} ${this._densityKey}`
  }

  /**
   * Returns columns filtered by appropriate column types for user.
   */
  @computed
  get filteredColumns(): (StaticColumnDetails & MutableColumnDetails)[] {
    const currentMember = this.getCurrentMember()
    if (!currentMember) {
      return []
    }
    const canViewPayout = this.canViewPayoutColumn(currentMember.user.publicId)
    const canViewTotal = this.canViewTotalColumn(currentMember.user.publicId)
    const canViewIsRequestedPro = this.canViewIsRequestedProColumn()
    const canSelectJobsForActions = this.canSelectJobsForActions()
    const canViewProInfo = this.canViewProInfo()
    const canViewClientInfo = this.canViewClientInfo()

    return this.columns.filter((column) =>
      this.isColumnAccessible(
        column,
        canViewPayout,
        canViewTotal,
        canViewIsRequestedPro,
        canSelectJobsForActions,
        canViewClientInfo,
        canViewProInfo,
      ),
    )
  }

  /**
   * Determines whether a column is accessible to a user.
   */
  isColumnAccessible(
    column: StaticColumnDetails & MutableColumnDetails,
    canViewPayout: boolean,
    canViewTotal: boolean,
    canViewIsRequestedPro: boolean,
    canSelectJobsForActions: boolean,
    canViewClientInfo: boolean,
    canViewProInfo: boolean,
  ) {
    switch (column.id) {
      case ColumnId.Checkbox:
        return canSelectJobsForActions
      case ColumnId.JobTitle:
        return true
      case ColumnId.JobShortId:
        return true
      case ColumnId.LegendName:
        return true
      case ColumnId.Workspace:
        return true
      case ColumnId.Status:
        return true
      case ColumnId.ProgressionStep:
        return true
      case ColumnId.JobScopeChangeRequestStatus:
        return true
      case ColumnId.ClientTeam:
        return true
      case ColumnId.ClientChampion:
        return true
      case ColumnId.ClientChampionEmail:
        return canViewClientInfo
      case ColumnId.ProChampion:
        return true
      case ColumnId.ProChampionEmail:
        return canViewProInfo
      case ColumnId.IsRequestedPro:
        return canViewIsRequestedPro
      case ColumnId.Payout:
        return canViewPayout
      case ColumnId.Total:
        return canViewTotal
      case ColumnId.TotalPaid:
        return false
      case ColumnId.Created:
        return true
      case ColumnId.Submitted:
        return true
      case ColumnId.Claimed:
        return true
      case ColumnId.Completed:
        return true
      case ColumnId.LastProMessageActivity:
        return true
      case ColumnId.LastClientMessageActivity:
        return true
      case ColumnId.LastRelationshipManagerMessageActivity:
        return true
      default:
        return false
    }
  }

  /**
   * Determine whether user should be allowed to view client information.
   */
  canViewClientInfo() {
    return this.getCurrentUserWorkspaceMemberships().some((m) =>
      m.member.hasPermission('VIEW_CLIENT_DETAILS'),
    )
  }

  /**
   * Determine whether user should be allowed to view pro champion information.
   */
  canViewProInfo() {
    return this.getCurrentUserWorkspaceMemberships().some((m) =>
      m.member.hasPermission('ADMIN_PRO_PI_VIEW'),
    )
  }

  /**
   * Determine whether user should be allowed to view payout column.
   */
  canViewPayoutColumn(id: Id) {
    const isUserTypePro =
      this.userDataStore.getByPublicId(id)?.userDataType === UserDataType.Pro
    const hasAdminViewPricing = this.getCurrentUserWorkspaceMemberships().some(
      (m) => m.member.hasPermission('ADMIN_PRO_CUT_VIEW'),
    )

    return isUserTypePro || hasAdminViewPricing
  }

  /**
   * Indicates whether the user is allowed to select jobs for actions.
   */
  canSelectJobsForActions() {
    const selectedWorkspaces = this.getSelectedWorkspaces()
    const currentMemberships = this.getCurrentUserWorkspaceMemberships()

    return selectedWorkspaces.length === 0
      ? this.getCurrentUserWorkspaceMemberships().every((m) =>
          m.member.hasPermission('ADMIN_JOB_LIST'),
        )
      : selectedWorkspaces.every((id) =>
          currentMemberships
            .find((m) => m.workspace.id === id)
            ?.member.hasPermission('ADMIN_JOB_LIST'),
        )
  }

  /**
   * Determine whether user should be allowed to view total column.
   */
  canViewTotalColumn(id: Id) {
    const isUserTypeUser =
      this.userDataStore.getByPublicId(id)?.userDataType === UserDataType.User
    const hasAdminViewPricing = this.getCurrentUserWorkspaceMemberships().some(
      (m) => m.member.hasPermission('ADMIN_VIEW_PRICING'),
    )

    return isUserTypeUser || hasAdminViewPricing
  }

  /**
   * Determine whether user should be allowed to view is requested pro column.
   */
  canViewIsRequestedProColumn() {
    return this.getCurrentUserWorkspaceMemberships().some((m) =>
      m.member.hasPermission('ADMIN_ACCESS'),
    )
  }

  /**
   * Returns only the mutable column details from the columns observable.
   */
  @computed
  get mutableSettings(): MutableColumnDetails[] {
    return this.columns.map((column) => {
      return {
        id: column.id,
        visible: column.visible,
      }
    })
  }

  /**
   * Returns value indicating whether all columns' visibility property are true.
   */
  @computed
  get allColumnsVisible() {
    return this.filteredColumns.every((column) => column.visible)
  }

  /**
   * Triggers download of jobs data organized into a CSV file.
   */
  exportJobsAsCSV = async () => {
    const progress = this.newAlert().saying('Job exporting in progress').show()
    const csvData: CSVData = await this.adapter.jobsExportData()

    progress
      .withState(AlertState.Success)
      .saying(
        'Job export completed!',
        'The exported file should begin downloading...',
      )
      .autoDismiss()
    csvDownload(csvData, 'jobs.csv')
  }

  /**
   * Sets column manager dropdown state to open.
   */
  @action.bound
  openColumnManager() {
    this.columnManagerOpen = true
  }

  /**
   * Sets column manager dropdown state to closed.
   */
  @action.bound
  closeColumnManager() {
    this.columnManagerOpen = false
    this.saveColumnSettings()
  }

  /**
   * Toggles column manager dropdown state.
   */
  @action.bound
  toggleColumnManager() {
    if (this.columnManagerOpen) {
      this.closeColumnManager()
    } else {
      this.openColumnManager()
    }
  }

  /**
   * Loads mutable column settings from local storage, updating columns observable.
   * Matches columns in local storage to those in JobsTableColumns, and ensures
   * columns observable has all columns, even if local storage does not.
   */
  @action.bound
  loadColumnSettings() {
    const columnList = JobsTableColumns
    const settings =
      (this.storage.getItem(this.userKey) as MutableColumnDetails[]) ||
      defaultSettings
    const density =
      (this.storage.getItem(this.densityKey) as ColumnDensity) ||
      ColumnDensity.Default

    const findColumn = (columnId: string) =>
      columnList.find((col) => col.id === columnId)

    this.columns = settings
      .map((column) => {
        const found = findColumn(column.id)
        if (!found) {
          return null
        }
        return {
          ...found,
          visible: found.modifyVisibility ? column.visible : true,
        }
      })
      .filter(isNotNil)

    if (this.columns.length < columnList.length) {
      const extraColumns = columnList
        .map((definition) => {
          const isAlreadyInColumnList = this.columns.some(
            (col) => col.id === definition.id,
          )
          if (isAlreadyInColumnList) {
            return null
          }
          return {
            ...definition,
            visible: true,
          }
        })
        .filter(isNotNil)

      this.columns.push(...extraColumns)
    }

    this.density = density

    // Ensure the action column is always pinned to the head of the column list.
    const actionIndex = this.columns.findIndex((col) => col.actionSelector)
    if (actionIndex === -1) {
      return
    }

    move(this.columns, actionIndex, 0)
  }

  /**
   * Saves mutable column settings to local storage.
   */
  @action.bound
  saveColumnSettings() {
    this.storage.setItem(this.userKey, this.mutableSettings)
  }

  /**
   * Returns index of a column using the column id.
   * @param columnId
   * @returns
   */
  private findIndex(columnId: string) {
    return this.columns.findIndex((col) => col.id === columnId)
  }

  /**
   * Sets column visibility state to true.
   * @param columnId
   */
  @action.bound
  showColumn(columnId: string) {
    const index = this.findIndex(columnId)
    this.columns[index].visible = true
  }

  /**
   * Sets column visibility state to false, as long as column modify visibility
   * property is true.
   * @param columnId
   */
  @action.bound
  hideColumn(columnId: string) {
    const index = this.findIndex(columnId)
    if (this.columns[index].modifyVisibility) {
      this.columns[index].visible = false
    }
  }

  @action.bound
  setDensity(density: ColumnDensity) {
    this.density = density
    this.storage.setItem(this.densityKey, this.density)
  }

  /**
   * Toggles column visibility state.
   * @param columnId
   */
  @action.bound
  toggleColumnVisibility(columnId: string) {
    const index = this.findIndex(columnId)
    if (this.columns[index].visible) {
      this.hideColumn(columnId)
    } else {
      this.showColumn(columnId)
    }
  }

  /**
   * Sets all columns' visible property to their opposite states.
   * Determines toggle orientation relative to visibility for all columns.
   * If a single column is not visible, toggle will set all columns visible.
   */
  @action.bound
  toggleAllColumnVisibility() {
    const allVisible = this.allColumnsVisible
    this.columns.forEach((column) => {
      if (allVisible) {
        this.hideColumn(column.id)
      } else {
        this.showColumn(column.id)
      }
    })
  }

  @action.bound
  updateColumnOrder(fromFilteredIndex: number, toFilteredIndex?: number) {
    if (!isNotNil(toFilteredIndex)) {
      return
    }
    const filteredColumn = this.filteredColumns[fromFilteredIndex]
    const toFilteredColumn = this.filteredColumns[toFilteredIndex]

    const fromIndex = this.columns.findIndex(
      (column) => column.id === filteredColumn.id,
    )
    const toIndex = this.columns.findIndex(
      (column) => column.id === toFilteredColumn.id,
    )

    const column = this.columns[fromIndex]

    this.columns.splice(fromIndex, 1)
    this.columns.splice(toIndex, 0, column)
  }
}
